// File: /src/components/ClientModalView.js

import React, { useState, forwardRef } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	Tabs,
	Tab,
	Box,
	Tooltip,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { FaEdit, FaTrashAlt, FaFileInvoice, FaQrcode, FaDownload } from 'react-icons/fa';
import ClientForm from '../components/ClientForm';
import ProjectsForm from '../components/ProjectsForm';
import InvoiceForm from '../components/InvoiceForm';
import PixQRCode from './PixQRCode';
import '../styles/ClientModal.css';
import PropTypes from 'prop-types';
import DocumentForm from './DocumentForm';
import { useConfirmDialog } from '../hooks/useConfirmDialog';

// Create ref-forwarding icon components
const IconComponent = forwardRef(({ icon: Icon, ...props }, ref) => (
	<div ref={ref} {...props}>
		<Icon />
	</div>
));

function ClientModalView({
	onClose,
	client,
	projects,
	invoices,
	tabIndex,
	handleTabChange,
	modalFunctions,
	user,
}) {
	const { showConfirm } = useConfirmDialog();
	
	const {
		openClientEditModal,
		handleDeleteClient,
		openProjectEditModal,
		openAddProjectModal,
		handleDeleteProject,
		openInvoiceEditModal,
		openAddInvoiceModal,
		handleDeleteInvoice,
		openAddDocumentModal,
		handleDocumentUpload,
		handleDocumentDownload,
		handleDeleteDocument,
		clientEditModalOpen,
		projectEditModalOpen,
		invoiceEditModalOpen,
		documentModalOpen,
		closeClientEditModal,
		closeProjectEditModal,
		closeInvoiceEditModal,
		closeDocumentModal,
		localClientForm,
		handleClientInputChange,
		handleClientSubmit,
		localProjectForm,
		handleProjectInputChange,
		handleProjectSubmit,
		isEditingProject,
		localInvoiceForm,
		handleInvoiceInputChange,
		handleInvoiceSubmit,
		isEditingInvoice,
		generateReceipt,
		documents,
	} = modalFunctions;

	const [pixModalOpen, setPixModalOpen] = useState(false);
	const [selectedInvoice, setSelectedInvoice] = useState(null);

	return (
		<>
			<Dialog open={true} onClose={onClose} fullWidth maxWidth="md">
				<DialogTitle>
					<div className="client-modal-header">
						<IconButton
							onClick={onClose}
							className="modal-close-btn"
						>
							<CloseIcon />
						</IconButton>
						<span className="client-modal-title">
							{client.name}
						</span>
						{tabIndex === 1 && (
							<button
								className="compact-add-button"
								onClick={openAddProjectModal}
							>
								Adicionar Projeto
							</button>
						)}
						{tabIndex === 2 && (
							<button
								className="compact-add-button"
								onClick={openAddInvoiceModal}
							>
								Adicionar Fatura
							</button>
						)}
						{tabIndex === 3 && (
							<button
								className="compact-add-button"
								onClick={openAddDocumentModal}
							>
								Anexar Documento
							</button>
						)}
					</div>
				</DialogTitle>

				<DialogContent dividers className="client-modal-content">
					<Tabs
						value={tabIndex}
						onChange={handleTabChange}
						variant="fullWidth"
						className="client-modal-tabs"
					>
						<Tab label="Cliente" className="client-modal-tab" />
						<Tab label="Projetos" className="client-modal-tab" />
						<Tab label="Faturas" className="client-modal-tab" />
						<Tab label="Documentos" className="client-modal-tab" /> {/* Nova aba */}
					</Tabs>

					{/* Aba Dados do Cliente */}
					<Box hidden={tabIndex !== 0} className="client-modal-section">
						<div className="client-modal-row">
							<div className="client-modal-data">
								<p>
									<strong>Email:</strong> {client.email}
								</p>
								<p>
									<strong>CPF/CNPJ:</strong> {client.cpf_cnpj}
								</p>
								<p>
									<strong>Telefone:</strong> {client.phone}
								</p>
								<p>
									<strong>Endereço:</strong> {client.address}
								</p>
							</div>
							<div className="action-icons">
								<Tooltip title="Editar Cliente">
									<IconComponent 
										icon={FaEdit}
										className="edit-icon"
										onClick={() => openClientEditModal(client)}
									/>
								</Tooltip>
							<Tooltip title="Excluir Cliente">
								<IconComponent
									icon={FaTrashAlt}
									className="delete-icon"
									onClick={async () => {
										const confirmed = await showConfirm({
											title: 'Excluir Cliente',
											message: 'Deseja realmente excluir este cliente? Todos os projetos, faturas e documentos associados também serão excluídos. Esta ação não pode ser desfeita.',
											confirmText: 'Excluir',
											cancelText: 'Cancelar',
											variant: 'danger',
										});
										if (confirmed) {
											handleDeleteClient();
										}
									}}
								/>
							</Tooltip>
							</div>
						</div>
						{client.archived && (
							<div className="archived-badge-container">
								<span className="archived-badge">
									Arquivado
								</span>
							</div>
						)}
					</Box>

					{/* Aba Projetos */}
					<Box hidden={tabIndex !== 1} className="client-modal-section">
						{projects && projects.length > 0 ? (
							projects.map((proj) => (
								<Box
									key={proj.id}
									mb={3}
									sx={{
										borderBottom: '5px solid #ddd',
										pb: 2,
										mt: 2,
									}}
									className={proj.archived ? 'archived-item' : ''}
								>
									<div className="client-modal-row">
										<div className="client-modal-data">
											<h3>{proj.name}</h3>
											<p>{proj.description}</p>
											<p>
												<strong>Status:</strong>{' '}
												<span
													className={`status-badge ${
														proj.status === 'pending'
															? 'status-pending'
															: proj.status === 'in_progress'
															? 'status-in-progress'
															: 'status-completed'
													}`}
												>
													{proj.status === 'pending'
														? 'PENDENTE'
														: proj.status === 'in_progress'
														? 'EM ANDAMENTO'
														: 'CONCLUÍDO'}
												</span>
											</p>
											<p>
												<strong>Valor:</strong>{' '}
												{proj.value ? proj.value : '-'}
											</p>
											<p>
												<strong>Data de Início:</strong>{' '}
												{proj.start_date
													? new Date(proj.start_date).toLocaleString('pt-BR', {
															dateStyle: 'short',
															timeStyle: 'short',
													  })
													: '-'}
											</p>
											<p>
												<strong>Data de Término:</strong>{' '}
												{proj.end_date
													? new Date(proj.end_date).toLocaleString('pt-BR', {
															dateStyle: 'short',
															timeStyle: 'short',
													  })
													: '-'}
											</p>
										</div>
										<div className="action-icons">
											<Tooltip title="Editar Projeto">
												<IconComponent 
													icon={FaEdit}
													className="edit-icon"
													onClick={() => openProjectEditModal(proj)}
												/>
											</Tooltip>
										<Tooltip title="Excluir Projeto">
											<IconComponent
												icon={FaTrashAlt}
												className="delete-icon"
												onClick={async () => {
													const confirmed = await showConfirm({
														title: 'Excluir Projeto',
														message: 'Deseja realmente excluir este projeto? Todas as faturas associadas também serão excluídas. Esta ação não pode ser desfeita.',
														confirmText: 'Excluir',
														cancelText: 'Cancelar',
														variant: 'danger',
													});
													if (confirmed) {
														handleDeleteProject(proj.id);
													}
												}}
											/>
										</Tooltip>
										</div>
									</div>
									{proj.archived && (
										<div className="archived-badge-container">
											<span className="archived-badge">
												Arquivado
											</span>
										</div>
									)}
								</Box>
							))
						) : (
							<p style={{ fontStyle: 'italic' }}>
								Nenhum projeto cadastrado para este cliente.
							</p>
						)}
					</Box>

					{/* Aba Faturas */}
					<Box hidden={tabIndex !== 2} className="client-modal-section">
						{projects && projects.length > 0 ? (
							projects.map((proj) => {
								const serviceInvoices = invoices.filter(
									(inv) => Number(inv.projectId) === Number(proj.id)
								);
								return (
									<Box
										key={proj.id}
										mb={2}
										sx={{
											borderBottom: '5px solid #ddd',
											pb: 2,
											mt: 2,
										}}
									>
										<div className="client-modal-row">
											<div className="client-modal-data">
												<h3>{proj.name}</h3>
											</div>
										</div>
										{serviceInvoices && serviceInvoices.length > 0 ? (
											serviceInvoices.map((inv) => (
												<Box
													key={inv.id}
													className={inv.archived ? 'archived-item' : ''}
												>
													<div className="client-modal-row">
														<div className="client-modal-data">
															<p>{inv.description}</p>
															<p>
																<strong>Status:</strong>{' '}
																<span
																	className={`status-badge ${
																		inv.status === 'pending'
																			? 'status-pending'
																			: inv.status === 'paid'
																			? 'status-paid'
																			: 'status-overdue'
																	}`}
																>
																	{inv.status === 'pending'
																		? 'PENDENTE'
																		: inv.status === 'paid'
																		? 'PAGO'
																		: 'ATRASADO'}
																</span>
															</p>
															<p>
																<strong>Valor:</strong>{' '}
																{inv.value ? inv.value : '-'}
															</p>
															<p>
																<strong>Data de Vencimento:</strong>{' '}
																{inv.due_date
																	? new Date(inv.due_date).toLocaleDateString('pt-BR')
																	: '-'}
															</p>
														</div>
														<div className="action-icons">
															{(inv.status === 'pending' || inv.status === 'overdue') && (
																<Tooltip title="Gerar QR Code PIX">
																	<div
																		role="button"
																		tabIndex={0}
																		style={{
																			display: 'inline-flex',
																			cursor: 'pointer',
																		}}
																		onClick={() => {
																			setSelectedInvoice(inv);
																			setPixModalOpen(true);
																		}}
																	>
																		<FaQrcode className="qrcode-icon" />
																	</div>
																</Tooltip>
															)}
															<Tooltip title="Editar Fatura">
																<IconComponent 
																	icon={FaEdit}
																	className="edit-icon"
																	onClick={() => openInvoiceEditModal(inv)}
																/>
															</Tooltip>
														<Tooltip title="Excluir Fatura">
															<IconComponent
																icon={FaTrashAlt}
																className="delete-icon"
																onClick={async () => {
																	const confirmed = await showConfirm({
																		title: 'Excluir Fatura',
																		message: 'Deseja realmente excluir esta fatura? Esta ação não pode ser desfeita.',
																		confirmText: 'Excluir',
																		cancelText: 'Cancelar',
																		variant: 'danger',
																	});
																	if (confirmed) {
																		handleDeleteInvoice(inv.id);
																	}
																}}
															/>
														</Tooltip>
															<Tooltip title="Emitir Recibo">
																<IconComponent 
																	icon={FaFileInvoice}
																	className="receipt-icon"
																	onClick={() => generateReceipt([inv.id])}
																/>
															</Tooltip>
														</div>
													</div>
													{inv.archived && (
														<div className="archived-badge-container">
															<span className="archived-badge">
																Arquivado
															</span>
														</div>
													)}
												</Box>
											))
										) : (
											<p style={{ fontStyle: 'italic' }}>
												Nenhuma fatura cadastrada para este projeto.
											</p>
										)}
									</Box>
								);
							})
						) : (
							<p style={{ fontStyle: 'italic' }}>
								Nenhum projeto cadastrado para este cliente.
							</p>
						)}
					</Box>

					{/* Nova Aba: Documentos */}
					<Box hidden={tabIndex !== 3} className="client-modal-section">
							{documents && documents.length > 0 ? (
									documents.map((doc) => (
											<Box
													key={doc.id}
													mb={3}
													sx={{
														borderBottom: '5px solid #ddd',
														pb: 2,
														mt: 2,
													}}
											>
												<div className="client-modal-row">
													<div className="client-modal-data">
														<h3>{doc.name || "Documento sem nome"}</h3>
														<p>
															<strong>Tipo:</strong>{' '}
															{doc.mimeType
																	? doc.mimeType.split('/')[1].toUpperCase()
																	: "Tipo desconhecido"}
														</p>
														{doc.size && (
																<p>
																	<strong>Tamanho:</strong>{' '}
																	{(doc.size / 1024).toFixed(2)} KB
																</p>
														)}
														{doc.createdAt && (
																<p>
																	<strong>Data de Upload:</strong>{' '}
																	{new Date(doc.createdAt).toLocaleString('pt-BR', {
																		dateStyle: 'short',
																		timeStyle: 'short',
																	})}
																</p>
														)}
													</div>
													<div className="action-icons">
														<Tooltip title="Download do Documento">
															<IconComponent
																	icon={FaDownload}
																	className="download-icon"
																	onClick={() => handleDocumentDownload(doc.id)}
															/>
														</Tooltip>
													<Tooltip title="Excluir Documento">
														<IconComponent
																icon={FaTrashAlt}
																className="delete-icon"
																onClick={async () => {
																	const confirmed = await showConfirm({
																		title: 'Excluir Documento',
																		message: 'Deseja realmente excluir este documento? Esta ação não pode ser desfeita.',
																		confirmText: 'Excluir',
																		cancelText: 'Cancelar',
																		variant: 'danger',
																	});
																	if (confirmed) {
																		handleDeleteDocument(doc.id);
																	}
																}}
														/>
													</Tooltip>
													</div>
												</div>
											</Box>
									))
							) : (
									<p style={{ fontStyle: 'italic' }}>
										Nenhum documento cadastrado para este cliente.
									</p>
							)}
					</Box>
				</DialogContent>
			</Dialog>

			{/* Modal de Edição de Cliente */}
			{clientEditModalOpen && (
				<Dialog
					open={clientEditModalOpen}
					onClose={closeClientEditModal}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>
						<div style={{ position: 'relative' }}>
							<IconButton
								onClick={closeClientEditModal}
								style={{
									position: 'absolute',
									top: 8,
									right: 8,
								}}
							>
								<CloseIcon />
							</IconButton>
						</div>
					</DialogTitle>
					<DialogContent>
						<ClientForm
							form={localClientForm}
							handleInputChange={handleClientInputChange}
							addOrUpdateClient={handleClientSubmit}
							editingClient={client}
						/>
					</DialogContent>
				</Dialog>
			)}

			{/* Modal de Edição de Projeto */}
			{projectEditModalOpen && (
				<Dialog
					open={projectEditModalOpen}
					onClose={closeProjectEditModal}
					fullWidth
					maxWidth="sm"
				>
					<DialogContent>
						<DialogTitle>
							<div style={{ position: 'relative' }}>
								<IconButton
									onClick={closeProjectEditModal}
									style={{
										position: 'absolute',
										top: 8,
										right: 8,
									}}
								>
									<CloseIcon />
								</IconButton>
							</div>
						</DialogTitle>
						<ProjectsForm
							form={localProjectForm}
							handleInputChange={handleProjectInputChange}
							addOrUpdateProject={handleProjectSubmit}
							editingProject={isEditingProject ? localProjectForm : null}
							clients={[client]} // Para exibição de dados do cliente
						/>
					</DialogContent>
				</Dialog>
			)}

			{/* Modal de Edição de Fatura */}
			{invoiceEditModalOpen && (
				<Dialog
					open={invoiceEditModalOpen}
					onClose={closeInvoiceEditModal}
					fullWidth
					maxWidth="sm"
				>
					<DialogContent>
						<DialogTitle>
							<div style={{ position: 'relative' }}>
								<IconButton
									onClick={closeInvoiceEditModal}
									style={{
										position: 'absolute',
										top: 8,
										right: 8,
									}}
								>
									<CloseIcon />
								</IconButton>
							</div>
						</DialogTitle>
						<InvoiceForm
							form={localInvoiceForm}
							handleInputChange={handleInvoiceInputChange}
							addOrUpdateInvoice={handleInvoiceSubmit}
							editingInvoice={isEditingInvoice ? localInvoiceForm : null}
							projects={projects}
						/>
					</DialogContent>
				</Dialog>
				)}

			{/* Modal de Upload de Documento */}
			{documentModalOpen && (
				<Dialog
					open={documentModalOpen}
					onClose={closeDocumentModal}  // Changed here
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle>
						<div style={{ position: 'relative' }}>
							Upload de Documento
							<IconButton
								onClick={closeDocumentModal}  // And changed here
								style={{
									position: 'absolute',
									top: 8,
									right: 8,
								}}
							>
								<CloseIcon />
							</IconButton>
						</div>
					</DialogTitle>
					<DialogContent>
						<DocumentForm handleSubmit={handleDocumentUpload} />
					</DialogContent>
				</Dialog>
			)}

			{/* Modal de QR Code PIX */}
			{pixModalOpen && selectedInvoice && (
				<Dialog
					open={pixModalOpen}
					onClose={() => {
						setPixModalOpen(false);
					}}
					maxWidth="sm"
					fullWidth
					className="qr-code-modal"
					PaperProps={{
						style: {
							margin: '16px',
							maxHeight: '90vh',
							overflow: 'auto',
						},
					}}
				>
					<DialogTitle>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
							}}
						>
							QR Code PIX
							<IconButton
								onClick={() => setPixModalOpen(false)}
								style={{
									padding: '8px',
								}}
							>
								<CloseIcon />
							</IconButton>
						</div>
					</DialogTitle>
					<DialogContent style={{ paddingBottom: '24px' }}>
						{!user ? (
							<div style={{ padding: '20px', textAlign: 'center' }}>
								<p>Carregando dados do usuário...</p>
								<span className="loading-spinner"></span>
							</div>
						) : !user.pixKey ? (
							<div style={{ padding: '20px', textAlign: 'center' }}>
								<p>⚠️ Chave PIX não configurada.</p>
								<p style={{ fontSize: '14px', color: '#666' }}>
									Por favor, configure sua chave PIX nas configurações da conta.
								</p>
							</div>
						) : (
							<PixQRCode
								invoice={selectedInvoice}
								pixKey={user.pixKey}
								client={client}
								user={user}
							/>
						)}
					</DialogContent>
				</Dialog>
			)}
		</>
	);
}

ClientModalView.propTypes = {
	user: PropTypes.shape({
		pixKey: PropTypes.string,
	}),
};

export default ClientModalView;
